import React from "react";
import DividerMotif from "../../components/Brand/DividerMotif";
import { PublicHeader } from "../../components/Layout/PublicHeader";
import { useDocumentMeta } from "../../lib/useDocumentMeta";

/*
 * Velaigal Terms & Conditions, treated as a real editorial document.
 *
 * The previous version dumped the legal copy into a <pre> block. This one
 * sets each numbered clause as a proper article section with Fraunces
 * headings, body copy in Instrument Sans, and the section divider motif
 * between major groups. The legal text itself is unchanged - only the
 * wrapper is restyled.
 */

interface Section {
  id: string;
  number: string;
  title: string;
  paragraphs: string[];
  /** Optional bullet list rendered after the paragraphs. */
  bullets?: string[];
}

const SECTIONS: Section[] = [
  {
    id: "definitions",
    number: "1",
    title: "Definitions",
    paragraphs: [],
    bullets: [
      '"Velaigal", "we", "us", "our": VELAIGAL',
      '"Service": our web apps, APIs, assessments, analytics, and related materials',
      '"Customer": organization or individual using the Service',
      '"User": anyone with an account (admins, recruiters, candidates)',
      '"Content": data you upload or generate (job descriptions, resumes, task submissions, results)',
    ],
  },
  {
    id: "account",
    number: "2",
    title: "Account and eligibility",
    paragraphs: [
      "You must provide accurate information and keep credentials secure. You are responsible for all activity under your account.",
    ],
  },
  {
    id: "acceptable-use",
    number: "3",
    title: "Acceptable use",
    paragraphs: ["You agree not to:"],
    bullets: [
      "Upload unlawful, discriminatory, or harmful content",
      "Attempt to bypass security or misuse APIs",
      "Reverse engineer or scrape at scale",
      "Use results to unlawfully discriminate or violate labor laws",
    ],
  },
  {
    id: "ai-outputs",
    number: "4",
    title: "AI outputs and human oversight",
    paragraphs: [
      "Our AI generates hiring tasks from job descriptions and pre-screens candidate task submissions, producing advisory scores, flags, and reports. These outputs are advisory only and do not make hiring decisions — the AI never auto-rejects a candidate. The recruiter reviews every application and makes all shortlist and reject decisions. You are responsible for final hiring decisions and for complying with applicable employment and anti-discrimination laws.",
    ],
  },
  {
    id: "data-privacy",
    number: "5",
    title: "Data and privacy",
    paragraphs: [
      "We process data per our Privacy Policy and any signed Data Processing Addendum (DPA). You represent that you have a lawful basis (e.g., consent or legitimate interests) to process candidate data.",
    ],
  },
  {
    id: "security",
    number: "6",
    title: "Security and compliance",
    paragraphs: [
      "We implement encrypted transport (TLS), password hashing with argon2id, role-based access controls, and audit logs, and aim to comply with applicable data protection laws including GDPR and India's DPDP Act (see Privacy Policy for details). We follow established security practices, but as an early-stage product we have not yet pursued formal certification such as SOC 2 or ISO 27001.",
    ],
  },
  {
    id: "integrations",
    number: "7",
    title: "Integrations and third parties",
    paragraphs: [
      "We use a small number of third-party service providers to run the Service — including AI processing, file storage, and transactional email (see Privacy Policy for the named subprocessors). These providers are subject to their own terms, and we are not liable for their systems.",
    ],
  },
  {
    id: "fees",
    number: "8",
    title: "Fees",
    paragraphs: [
      "The Service is currently free to use during early access. There is no auto-renewing subscription today. If paid plans are introduced in the future, these Terms will be updated and users will be notified before any charges apply.",
    ],
  },
  {
    id: "ip",
    number: "9",
    title: "Intellectual property",
    paragraphs: [
      "We retain all rights to the Service, documentation, algorithms, and models. You retain rights to your Content. You grant us a limited license to process it to provide the Service.",
    ],
  },
  {
    id: "confidentiality",
    number: "10",
    title: "Confidentiality",
    paragraphs: [
      "Each party will protect the other's confidential information and use it only as necessary to deliver or receive the Service.",
    ],
  },
  {
    id: "service-levels",
    number: "11",
    title: "Service levels and changes",
    paragraphs: [
      "Provided on a reasonable-efforts basis during early access; we don't offer an uptime SLA today. We may improve or modify features; material changes will be communicated with reasonable notice.",
    ],
  },
  {
    id: "warranties",
    number: "12",
    title: "Warranties and disclaimers",
    paragraphs: [
      'The Service is provided "as is". We disclaim implied warranties to the extent permitted by law. We do not guarantee specific hiring outcomes.',
    ],
  },
  {
    id: "liability",
    number: "13",
    title: "Limitation of liability",
    paragraphs: [
      "To the maximum extent permitted by law, our aggregate liability is limited to the fees paid in the 12 months before the claim. We are not liable for indirect or consequential damages (loss of profits, data, or business).",
    ],
  },
  {
    id: "indemnity",
    number: "14",
    title: "Indemnity",
    paragraphs: [
      "You agree to indemnify us for claims arising from your unlawful use of the Service or violation of these Terms.",
    ],
  },
  {
    id: "termination",
    number: "15",
    title: "Suspension and termination",
    paragraphs: [
      "We may suspend or terminate access for breach of these Terms or security risk. You may stop using the Service at any time. Upon termination, we will retain or delete data per the Privacy Policy and your settings.",
    ],
  },
  {
    id: "governing-law",
    number: "16",
    title: "Governing law and dispute resolution",
    paragraphs: [
      "These Terms are governed by the laws of India (Tamil Nadu), excluding conflict-of-law rules. Disputes will be resolved via confidential arbitration in Tamil Nadu, India, unless your enterprise contract states otherwise.",
    ],
  },
  {
    id: "changes",
    number: "17",
    title: "Changes to terms",
    paragraphs: [
      'We may update these Terms; we will post the new version with a new "Last updated" date. Continued use constitutes acceptance.',
    ],
  },
  {
    id: "contact",
    number: "18",
    title: "Contact",
    paragraphs: [
      "VELAIGAL",
      "Support email: im.vmanoj2002@gmail.com",
      "Phone / WhatsApp: +91 8220 243 513",
    ],
  },
];

const TermsAndConditions: React.FC = () => {
  useDocumentMeta(
    "Terms and Conditions · Velaigal",
    "The terms that govern access to and use of Velaigal's platform for HR teams, recruiters, and candidates."
  );

  return (
    <div className="min-h-screen bg-cream text-ink">
      <PublicHeader variant="slim" />

      {/* Editorial article body. max-w-[720px] gives ~70-character measure
       * for body copy - the comfortable reading width of a real magazine. */}
      <main className="max-w-[720px] mx-auto px-6 md:px-8 py-16 md:py-20">
        <article className="bg-paper border border-hairline rounded-md px-8 py-16 md:px-14 md:py-20">
          {/* Document head */}
          <header>
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Legal · Velaigal
            </span>
            <h1
              className="font-display text-4xl font-medium text-ink leading-[1.05] tracking-tight mt-4"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
            >
              Terms and conditions
            </h1>
            <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-6" />
            <p className="mt-6 text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Last updated · 29 August 2026
            </p>
            <p className="mt-8 text-base text-ink-secondary leading-relaxed">
              Welcome to Velaigal. By accessing or using our platform, you
              agree to these Terms and Conditions ("Terms").
            </p>
          </header>

          {/* DividerMotif between document head and clauses */}
          <DividerMotif className="mt-12" />

          {/* Numbered sections. Render a section divider after every 6th
           * clause to give the document its editorial rhythm without
           * cluttering every break. */}
          <div className="mt-12 space-y-12">
            {SECTIONS.map((section, idx) => (
              <React.Fragment key={section.id}>
                <section id={section.id} className="scroll-mt-24">
                  <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
                    Section {section.number}
                  </span>
                  <h2
                    className="font-display text-xl text-ink leading-snug mt-3"
                    style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
                  >
                    {section.title}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p
                      key={pIdx}
                      className="mt-4 text-base text-ink-secondary leading-relaxed"
                    >
                      {para}
                    </p>
                  ))}
                  {section.bullets && (
                    <ul className="mt-4 space-y-2">
                      {section.bullets.map((item, bIdx) => (
                        <li
                          key={bIdx}
                          className="flex items-start gap-3 text-base text-ink-secondary leading-relaxed"
                        >
                          <span
                            aria-hidden="true"
                            className="mt-3 block w-3 h-px bg-saffron shrink-0"
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
                {/* Major-group divider every 6 sections (after 6 and 12) */}
                {(idx === 5 || idx === 11) && (
                  <DividerMotif className="py-2" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Editorial signature line */}
          <footer className="mt-20 pt-10 border-t border-hairline">
            <p className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium text-center">
              Velaigal · Tamil Nadu, India
            </p>
          </footer>
        </article>
      </main>
    </div>
  );
};

export default TermsAndConditions;
